import React, { useState, useRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCloudIcon, FileIcon, XIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
import Papa from 'papaparse';
import { BulkPaymentPreview } from './BulkPaymentPreview';
const TEMPLATE_COLUMNS = [{
  key: 'customerName',
  label: 'Customer Name',
  required: true
}, {
  key: 'email',
  label: 'Email',
  required: true
}, {
  key: 'amount',
  label: 'Amount',
  required: true
}, {
  key: 'description',
  label: 'Description',
  required: true
}, {
  key: 'phone',
  label: 'Phone',
  required: false
}, {
  key: 'currency',
  label: 'Currency',
  required: false
}, {
  key: 'reference',
  label: 'Reference',
  required: false
}, {
  key: 'dueDate',
  label: 'Due Date',
  required: false
}];
interface BulkPaymentUploadProps {
  onBatchCreated: () => void;
  simplified?: boolean;
}
export function BulkPaymentUpload({
  onBatchCreated,
  simplified = false
}: BulkPaymentUploadProps) {
  const {
    t
  } = useTranslation('payments');
  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStep, setUploadStep] = useState<'upload' | 'preview' | 'confirm'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setParseError(null);
    const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
    if (fileExt !== 'csv') {
      setParseError('Only CSV files are supported at this time');
      return;
    }
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        if (results.errors.length > 0) {
          setParseError(`Error parsing file: ${results.errors[0].message}`);
          return;
        }
        const requiredFields = ['customerName', 'email', 'amount', 'description'];
        const headers = Object.keys(results.data[0] || {});
        const missingFields = requiredFields.filter(field => !headers.includes(field));
        if (missingFields.length > 0) {
          setParseError(`Missing required fields: ${missingFields.join(', ')}`);
          return;
        }
        setParsedData(results.data);
        setUploadStep('preview');
      },
      error: error => {
        setParseError(`Error parsing file: ${error.message}`);
      }
    });
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(droppedFile);
      fileInputRef.current.files = dataTransfer.files;
    }
    handleFileChange({
      target: {
        files: e.dataTransfer.files
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  const handleRemoveFile = () => {
    setFile(null);
    setParsedData(null);
    setParseError(null);
    setUploadStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleConfirmBatch = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      onBatchCreated();
    }, 1500);
  };
  const handleDownloadTemplate = () => {
    const templateHeaders = 'customerName,email,phone,amount,currency,description,reference,dueDate\n';
    const tenantCurrency = getTenant().currency.code;
    const templateData = `John Doe,john@example.com,+1234567890,100.00,${tenantCurrency},Monthly subscription,INV-001,05/31/2026\n` + `Jane Smith,jane@example.com,+0987654321,250.00,${tenantCurrency},Consulting services,INV-002,06/15/2026`;
    const blob = new Blob([templateHeaders + templateData], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_payment_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  if (uploadStep === 'preview' && parsedData) {
    return <BulkPaymentPreview data={parsedData} onBack={() => setUploadStep('upload')} onConfirm={handleConfirmBatch} isSubmitting={isSubmitting} data-id="element-6915" />;
  }
  return <div className="space-y-6" data-id="element-6916">
      {/* Template download */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex items-center justify-between" data-id="element-6917">
        <div className="flex items-center" data-id="element-6918">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-6919">
            <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-6920" />
          </div>
          <div data-id="element-6921">
            <p className="text-sm font-medium text-gray-900" data-id="element-6922">
              {t('components.BulkPaymentUpload.download_template')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-6923">
              {t('components.BulkPaymentUpload.use_this_csv_template_to_format')}
            </p>
          </div>
        </div>
        <button type="button" onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex-shrink-0 ml-4" data-id="element-6924">
          <DownloadIcon size={16} className="mr-2" data-id="element-6925" />
          {t('components.BulkPaymentUpload.download_csv')}
        </button>
      </div>

      {/* Dropzone */}
      <div onDragOver={handleDragOver} onDrop={handleDrop} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-all ${parseError ? 'border-red-300 bg-red-50' : isDragging ? 'border-[#E2231A] bg-[#E2231A]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-6926">
        <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleFileChange} accept=".csv" ref={fileInputRef} data-id="element-6927" />

        {file ? <div className="flex flex-col items-center" data-id="element-6928">
            <div className="flex items-center bg-white rounded-lg px-4 py-2.5 mb-3 border border-gray-200 shadow-sm" data-id="element-6929">
              <FileIcon size={18} className="text-[#9F140F] mr-2.5" data-id="element-6930" />
              <span className="text-sm font-medium text-gray-700" data-id="element-6931">
                {file.name}
              </span>
              <button className="ml-3 text-gray-400 hover:text-red-500 transition-colors" onClick={e => {
            e.stopPropagation();
            handleRemoveFile();
          }} aria-label="Remove file" data-id="element-6932">
                <XIcon size={14} aria-hidden="true" data-id="element-6933" />
              </button>
            </div>
            {parseError ? <div className="flex items-center text-red-600 text-sm" data-id="element-6934">
                <AlertCircleIcon size={16} className="mr-2" data-id="element-6935" />
                <span data-id="element-6936">{parseError}</span>
              </div> : <div className="flex items-center text-green-600 text-sm" data-id="element-6937">
                <CheckCircleIcon size={16} className="mr-2" data-id="element-6938" />
                <span data-id="element-6939">
                  {t('components.BulkPaymentUpload.file_ready_click_preview_batch_below')}
                </span>
              </div>}
          </div> : <div className="flex flex-col items-center" data-id="element-6940">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#E2231A]/10' : 'bg-gray-100'}`} data-id="element-6941">
              <UploadCloudIcon size={28} className={isDragging ? 'text-[#9F140F]' : 'text-gray-400'} data-id="element-6942" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-6943">
              {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-6944">
              {t('components.BulkPaymentUpload.or_click_to_browse')}
            </p>
            <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-6945">
              {t('components.BulkPaymentUpload.supported_csv')}
            </span>
          </div>}
      </div>

      {/* Preview button */}
      {file && !parseError && <div className="flex justify-end" data-id="element-6946">
          <button type="button" onClick={() => setUploadStep('preview')} className="px-5 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6947">
            {t('components.BulkPaymentPreview.preview_batch')}
          </button>
        </div>}

      {/* Field reference */}
      {!file && <div data-id="element-6948">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6949">
            {t('components.BulkPaymentUpload.expected_fields')}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-6950">
            {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-6951">
                <span className="font-medium text-gray-800 mr-1" data-id="element-6952">
                  {col.label}
                </span>
                {col.required && <span className="text-red-400 text-[10px]" data-id="element-6953">*</span>}
              </div>)}
          </div>
        </div>}
    </div>;
}